import React, { useState, useRef, useEffect } from 'react';
import {
  Download, Rocket, ChevronDown, Github, Gitlab, Cloud, Loader2,
  ExternalLink, Copy, History,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PROVIDER_LABEL = {
  github: 'GitHub', gitlab: 'GitLab', netlify: 'Netlify', vercel: 'Vercel',
};

/**
 * ProjectActions — header dropdown for actions on the *currently-loaded* project:
 *  • Download ZIP   (always available)
 *  • Deploy → GitHub | GitLab | Netlify | Vercel
 *  • Deploy History → opens a flyout listing every successful deploy
 *
 * Each deploy reads the user's stored service token from
 * `/api/gpt-pilot/settings` (server-side), so this UI never holds secrets.
 * Successful deploys are persisted to Mongo via `/projects/{id}/deploys`
 * so the last URL survives a page refresh.
 */
export default function ProjectActions({ jobId, disabled }) {
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(null); // null | 'zip' | 'github' | 'gitlab' | 'netlify' | 'vercel'
  const [lastDeploy, setLastDeploy] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const wrapRef = useRef(null);

  // Load persisted deploy history whenever the active project changes.
  useEffect(() => {
    if (!jobId) {
      setLastDeploy(null);
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/deploys`, { headers: authHeader() });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setLastDeploy(data.last_deploy || null);
        setHistory(Array.isArray(data.deploys) ? data.deploys : []);
      } catch { /* silent — non-essential */ }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenu(false);
        setHistoryOpen(false);
      }
    };
    if (menu || historyOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menu, historyOpen]);

  const reloadHistory = async () => {
    if (!jobId) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/deploys`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setLastDeploy(data.last_deploy || null);
        setHistory(Array.isArray(data.deploys) ? data.deploys : []);
      }
    } finally { setHistoryLoading(false); }
  };

  const downloadZip = async () => {
    if (!jobId || busy) return;
    setBusy('zip');
    setMenu(false);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/download`, { headers: authHeader() });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.slice(0, 200) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Prefer filename from Content-Disposition.
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `snagcart-${jobId.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Project ZIP downloaded');
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const deploy = async (provider) => {
    if (!jobId || busy) return;
    setBusy(provider);
    setMenu(false);
    const tid = toast.loading(`Deploying to ${PROVIDER_LABEL[provider]}…`);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}/deploy/${provider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const url = data.url || null;
      if (url) {
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        toast.success(`Deployed to ${PROVIDER_LABEL[provider]} — URL copied`, { id: tid });
      } else {
        toast.success(`Deployed to ${PROVIDER_LABEL[provider]}`, { id: tid });
      }
      // Refresh persisted history from the server so the count is accurate.
      reloadHistory();
    } catch (e) {
      toast.error(`${PROVIDER_LABEL[provider]} deploy failed: ${e.message}`, { id: tid });
    } finally {
      setBusy(null);
    }
  };

  const PROVIDERS = [
    { id: 'github',  label: 'GitHub',  icon: Github,  hint: 'Push code to a new repo' },
    { id: 'gitlab',  label: 'GitLab',  icon: Gitlab,  hint: 'Push code to a new project' },
    { id: 'netlify', label: 'Netlify', icon: Cloud,   hint: 'Deploy built dist/ live' },
    { id: 'vercel',  label: 'Vercel',  icon: Cloud,   hint: 'Deploy built dist/ live' },
  ];

  return (
    <div ref={wrapRef} className="relative flex items-center gap-2" data-testid="project-actions">
      <button
        type="button"
        onClick={downloadZip}
        disabled={disabled || busy === 'zip' || !jobId}
        data-testid="action-download-zip"
        title="Download a ZIP of the entire project source"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-zinc-300 hover:text-white border border-white/[0.08] hover:border-white/[0.16] hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {busy === 'zip' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">ZIP</span>
      </button>

      <button
        type="button"
        onClick={() => { setMenu((m) => !m); setHistoryOpen(false); }}
        disabled={disabled || !!busy || !jobId}
        data-testid="action-deploy-toggle"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {busy && busy !== 'zip' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">Deploy</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      <button
        type="button"
        onClick={() => { setHistoryOpen((h) => !h); setMenu(false); reloadHistory(); }}
        disabled={disabled || !jobId}
        data-testid="action-history-toggle"
        title="View deploy history"
        className="relative inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border border-white/[0.08] text-zinc-300 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <History className="w-3.5 h-3.5" />
        {history.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-500 text-[9px] font-bold text-white flex items-center justify-center" data-testid="action-history-badge">
            {history.length > 99 ? '99+' : history.length}
          </span>
        )}
      </button>

      {menu && (
        <div
          className="absolute right-0 top-full mt-2 w-[300px] bg-[#0d1117] border border-white/[0.08] rounded-xl shadow-2xl z-40 overflow-hidden"
          data-testid="action-deploy-menu"
        >
          <div className="px-3 py-2 border-b border-white/[0.06]">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Deploy this project</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">Uses tokens you stored in Settings → Services.</p>
          </div>
          {PROVIDERS.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => deploy(p.id)}
                disabled={!!busy}
                data-testid={`action-deploy-${p.id}`}
                className="w-full text-left px-3 py-2.5 hover:bg-white/[0.04] border-b border-white/[0.04] last:border-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-md bg-white/[0.04] border border-white/[0.08] flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-zinc-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium">{p.label}</p>
                    <p className="text-[11px] text-zinc-500">{p.hint}</p>
                  </div>
                </div>
              </button>
            );
          })}
          {lastDeploy && lastDeploy.url && (
            <div className="px-3 py-2.5 border-t border-white/[0.06] bg-emerald-500/[0.04]" data-testid="action-deploy-last">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold mb-1">
                Last deploy ({PROVIDER_LABEL[lastDeploy.provider] || lastDeploy.provider})
              </p>
              <div className="flex items-center gap-1.5 text-[11px]">
                <a
                  href={lastDeploy.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-emerald-200 hover:text-emerald-100 truncate flex-1 flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  <span className="truncate">{lastDeploy.url}</span>
                </a>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(lastDeploy.url);
                      toast.success('URL copied');
                    } catch {
                      toast.error('Copy failed');
                    }
                  }}
                  data-testid="action-deploy-copy"
                  className="p-1 rounded hover:bg-white/[0.06] text-zinc-400 hover:text-white"
                  title="Copy URL"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {historyOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] bg-[#0d1117] border border-white/[0.08] rounded-xl shadow-2xl z-40 overflow-hidden"
          data-testid="action-history-menu"
        >
          <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Deploy history</p>
            <button
              onClick={reloadHistory}
              data-testid="action-history-refresh"
              className="text-zinc-500 hover:text-zinc-200 p-1 rounded"
              title="Refresh"
            >
              {historyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3 rotate-180" />}
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {history.length === 0 ? (
              <div className="text-center py-8 text-xs text-zinc-500 italic">No deploys yet for this project.</div>
            ) : (
              history.slice().reverse().map((d, i) => {
                const when = d.at ? new Date(d.at) : null;
                return (
                  <div
                    key={`${d.at}-${i}`}
                    className="px-3 py-2.5 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]"
                    data-testid={`action-history-row-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">
                        {PROVIDER_LABEL[d.provider] || d.provider}
                      </span>
                      <span className="text-[11px] text-zinc-500 flex-1 truncate">
                        {when ? when.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    {d.url && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-emerald-200 hover:text-emerald-100 truncate flex-1 flex items-center gap-1"
                          data-testid={`action-history-link-${i}`}
                        >
                          <ExternalLink className="w-3 h-3 shrink-0" />
                          <span className="truncate">{d.url}</span>
                        </a>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(d.url);
                              toast.success('URL copied');
                            } catch {
                              toast.error('Copy failed');
                            }
                          }}
                          className="p-1 rounded hover:bg-white/[0.06] text-zinc-400 hover:text-white"
                          title="Copy URL"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
